import React, { useMemo } from "react";

import { CalendarCheck, CheckCircle2, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

const formatClaimedAt = (claimedAt, locale) => {
  if (!claimedAt) return null;
  const date = new Date(claimedAt);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

const ClaimedState = ({ claimedAt }) => {
  const { t, i18n } = useTranslation(["walletOffer"]);
  const locale = i18n?.language;
  const formattedClaimedAt = useMemo(
    () => formatClaimedAt(claimedAt, locale),
    [claimedAt, locale],
  );

  return (
    <section
      role="status"
      className="animate-cert-card-in rounded-3xl border border-gray-200 bg-white p-6 text-center shadow-[0_24px_60px_-28px_rgba(16,24,40,0.25)] sm:p-10"
    >
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/60">
        <CheckCircle2 className="h-8 w-8" strokeWidth={2} aria-hidden="true" />
      </span>

      <h1 className="mt-6 text-xl font-bold tracking-tight text-cfHeadingText sm:text-2xl">
        {t("walletOffer:claimed_title")}
      </h1>
      <p className="mx-auto mb-0 mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
        {t("walletOffer:claimed_description")}
      </p>

      {formattedClaimedAt ? (
        <div className="mx-auto mt-6 flex max-w-sm items-center gap-3 rounded-2xl bg-gray-50 p-[1rem] text-left ring-1 ring-gray-100">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cfTableHeaderBackgroundColor text-cfPrimary">
            <CalendarCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
              {t("walletOffer:claimed_at_label")}
            </span>
            <time
              dateTime={claimedAt}
              className="block text-sm font-semibold text-cfHeadingText"
            >
              {formattedClaimedAt}
            </time>
          </span>
        </div>
      ) : null}

      <p className="mx-auto mb-0 mt-6 text-xs leading-relaxed text-gray-500">
        <Info
          className="-mt-0.5 mr-1 inline-block h-3.5 w-3.5 align-middle text-cfPrimary"
          aria-hidden="true"
        />
        {t("walletOffer:claimed_help")}
      </p>
    </section>
  );
};

export default ClaimedState;
